/* eslint-disable @typescript-eslint/no-throw-literal */
import queryString from "query-string";
import axios, {
	AxiosError,
	AxiosRequestConfig,
	InternalAxiosRequestConfig,
} from "axios";
import { toast } from "@onair/minimal-ui/snackbar";
import { ErrorCode, getErrorMessage } from "@onair/repositories";
import { ApiVersion } from "@onair/types/enums";
import { getSession } from "next-auth/react";
import { isSSR } from "./common";

const API_URL = process.env.NEXT_PUBLIC_API_URL;
const WEB_URL = process.env.NEXT_PUBLIC_WEB_URL;

export const axiosInstance = axios.create({
	baseURL: API_URL,
	withCredentials: true,
	timeout: 300000,
	headers: {
		"Content-Type": "application/json",
	},
});

const handleAxiosResponseError = async (
	error: AxiosError<{ message: string; code: string }>,
) => {
	const originalRequest: InternalAxiosRequestConfig & {
		_retry?: boolean;
	} = error?.config!;

	const errorCode = error.response?.data?.code;
	let message =
		error.response?.data?.message ||
		error.message ||
		"Đã có sự cố xảy ra, vui lòng thử lại sau.";

	if (errorCode) {
		message = getErrorMessage(errorCode, message);
	}

	if (process.env.NEXT_PUBLIC_APP_ENV !== "production") {
		console.error("DEV ~ Call API Error:", {
			message,
			path: error.request?.path,
		});
	}

	if (error && error?.status == 403) {
		if (
			typeof window !== "undefined" &&
			error.config?.method === "get" &&
			!error.config.url?.includes("export")
		) {
			toast.error("Bạn không có quyền truy cập trang này");
			const url = new URL(window.location.href);
			url.searchParams.set("_error", "403");
			window.history.replaceState({}, "", url.toString());
			return Promise.reject(error);
		}

		if (error.config?.method !== "get") {
			toast.error(
				error.response?.data.code === (ErrorCode?.NOT_OWNER || "NOT_OWNER")
					? "Bạn không phải là chủ sở hữu của tài nguyên này"
					: "Bạn không có quyền thực hiện hành động này",
			);
			return Promise.reject(error);
		}
	}

	if (
		error.response &&
		error.response.status === 401 &&
		!originalRequest._retry
	) {
		originalRequest._retry = true;

		let sessionUrl = `${WEB_URL}/api/auth/session`;
		if (typeof window !== "undefined") {
			sessionUrl = `${window.location.origin}/api/auth/session`;
		}

		// This will trigger the refresh token function in the next-auth config we configured
		await axiosInstance.get(sessionUrl);

		return axiosInstance(originalRequest);
	}

	const ctMessage =
		message === "Network Error"
			? "Không có kết nối mạng, vui lòng thử lại"
			: message;
	if (typeof window !== "undefined") {
		toast.dismiss(error.request?.path);
		toast.error(ctMessage, {
			id: error.request?.path ?? "--",
		});
	}

	return Promise.reject(error);
};

axiosInstance.defaults.timeout = 300000;

axiosInstance.interceptors.response.use((res) => res, handleAxiosResponseError);

const fetcher = async <T>(
	url: string,
	configs: AxiosRequestConfig = {},
	next?: (error?: Error | null, data?: T) => void,
	apiVersion?: ApiVersion,
): Promise<T> => {
	try {
		const version = apiVersion ?? ApiVersion.V1;
		const baseURL = `${API_URL}/${version}`;
		const options =
			!configs.method || configs.method === "get"
				? {
						...configs,
						baseURL,
						url: queryString.stringifyUrl(
							{ url, query: { ...configs.data } },
							{ encode: true, skipEmptyString: true, skipNull: true },
						),
						method: "get",
					}
				: {
						...configs,
						baseURL,
						url,
					};

		// const session = await getSession();

		// if (session?.accessToken) {
		// 	options.headers = {
		// 		...options.headers,
		// 		Authorization: `Bearer ${session?.accessToken}`,
		// 	};
		// }

		const response = await axiosInstance(options);

		// return response;
		if (response.status === 204 || response.statusText === "No Content") {
			next?.(null, {} as T);
			return {} as T;
		}

		if (response.status !== 200 && response.status !== 201) {
			throw response;
		}

		const { data: resData = {} } = response;

		next?.(null, resData);
		return resData;
	} catch (error) {
		next?.(error as Error);
		throw error;
	}
};

export default fetcher;
